import type { CoachAttentionSignal } from "@/hooks/useCoachAttentionSignals";

export interface CoachPortfolioTheme {
  id: string;
  title: string;
  priorityLabel: string;
  whyNow: string;
  leaderTieBack: {
    leaderId: string;
    leaderName: string;
    route: string;
    currentLeader: boolean;
  };
  supportingSignals: string[];
}

export interface CoachPortfolioIntelligence {
  title: string;
  themes: CoachPortfolioTheme[];
  confidenceLabel: "low" | "medium" | "high";
  confidenceNote: string;
  emptyStateReason: string;
}

const SEVERITY_PRIORITY: Record<CoachAttentionSignal["severity"], number> = {
  high: 0,
  medium: 1,
  low: 2,
};

function titleForSignalType(signalType: CoachAttentionSignal["signalType"]) {
  switch (signalType) {
    case "engagement_trend":
      return "Engagement drift is rising";
    case "sequence_check":
      return "Sequencing pressure is surfacing";
    case "graduation_review":
      return "Graduation candidates are stacking up";
    case "prolonged_practice":
      return "Extended focus is lingering";
    default:
      return "Portfolio signal is emerging";
  }
}

function priorityLabelForSignals(signals: CoachAttentionSignal[]) {
  if (signals.some((signal) => signal.severity === "high")) return "High priority";
  if (signals.length >= 2) return "Medium priority";
  return "Watch";
}

function groupWhyNow(signals: CoachAttentionSignal[]) {
  const primary = signals[0];
  const reason = primary.readiness?.reasonSummary || primary.reason;
  return signals.length > 1 ? `${signals.length} leaders are surfacing this theme now. ${reason}` : reason;
}

function groupSupportingSignals(signals: CoachAttentionSignal[]) {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const signal of signals) {
    for (const part of signal.readiness?.supportingSignals || [signal.reason]) {
      const normalized = part.trim();
      if (!normalized || seen.has(normalized)) continue;
      seen.add(normalized);
      result.push(normalized);
      if (result.length >= 3) return result;
    }
  }
  return result;
}

function pickLeaderTieBack(signals: CoachAttentionSignal[], currentLeaderId?: string | null) {
  const currentLeaderSignal = currentLeaderId
    ? signals.find((signal) => signal.leaderId === currentLeaderId)
    : null;
  const anchor = currentLeaderSignal
    || [...signals].sort((a, b) => SEVERITY_PRIORITY[a.severity] - SEVERITY_PRIORITY[b.severity])[0];

  return {
    leaderId: anchor.leaderId,
    leaderName: anchor.leaderName,
    route: anchor.ctaRoute,
    currentLeader: anchor.leaderId === currentLeaderId,
  };
}

export function buildCoachPortfolioIntelligence(params: {
  signals: CoachAttentionSignal[];
  currentLeaderId?: string | null;
}): CoachPortfolioIntelligence {
  if (params.signals.length === 0) {
    return {
      title: "Portfolio Intelligence",
      themes: [],
      confidenceLabel: "low",
      confidenceNote: "Signal is limited, so keep this read provisional.",
      emptyStateReason: "Portfolio signal is still forming, so there is not enough evidence to identify a cross-leader theme yet.",
    };
  }

  const groups = new Map<CoachAttentionSignal["signalType"], CoachAttentionSignal[]>();
  for (const signal of params.signals) {
    const group = groups.get(signal.signalType) || [];
    group.push(signal);
    groups.set(signal.signalType, group);
  }

  const themes = Array.from(groups.entries())
    .map(([signalType, signals]) => ({
      id: signalType,
      title: titleForSignalType(signalType),
      priorityLabel: priorityLabelForSignals(signals),
      whyNow: groupWhyNow(signals),
      leaderTieBack: pickLeaderTieBack(signals, params.currentLeaderId),
      supportingSignals: groupSupportingSignals(signals),
      severityRank: Math.min(...signals.map((signal) => SEVERITY_PRIORITY[signal.severity])),
      impactedLeaderCount: new Set(signals.map((signal) => signal.leaderId)).size,
    }))
    .sort((a, b) => {
      if (a.severityRank !== b.severityRank) return a.severityRank - b.severityRank;
      return b.impactedLeaderCount - a.impactedLeaderCount;
    })
    .slice(0, 3)
    .map(({ severityRank: _severityRank, impactedLeaderCount: _impactedLeaderCount, ...theme }) => theme);

  const confidenceLabel: CoachPortfolioIntelligence["confidenceLabel"] =
    params.signals.length >= 3 && groups.size >= 2 ? "high" : "medium";

  return {
    title: "Portfolio Intelligence",
    themes,
    confidenceLabel,
    confidenceNote: confidenceLabel === "high"
      ? "Multiple portfolio signals are reinforcing the same story right now."
      : "This is a compact portfolio read from the current attention signals; validate it against the live book if conditions change.",
    emptyStateReason: "",
  };
}
